import React from 'react';
import { Box, Grid, Link as MuiLink, Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import Slider from './Slider';
import './Home.css';

// Images
import i1 from '../assets/i1.png';
import i2 from '../assets/i2.png';
import heroImage from '../assets/landing1.png';

// Icons
import rugIcon from '../assets/rug-icon.png';
import bambooIcon from '../assets/bamboo-icon.png';
import basketryIcon from '../assets/basketry-icon.png';
import stitchingIcon from '../assets/stitching-icon.png';

// Intervention Data
const interventionData = [
  { name: 'Rugs', icon: rugIcon, path: '/intervention/rugs' },
  { name: 'Bamboo', icon: bambooIcon, path: '/intervention/bamboo' },
  { name: 'Basketry', icon: basketryIcon, path: '/intervention/basketry' },
  { name: 'Stitching', icon: stitchingIcon, path: '/intervention/stitching' },
];

// Intervention Item Component
const InterventionItem = ({ name, icon, path }) => (
  <Box textAlign="center" sx={{ mb: { xs: 3, md: 5 } }}>
    <MuiLink 
      component={Link} 
      to={path} 
      underline="none"
      sx={{ textDecoration: 'none' }}
    >
      <Box 
        sx={{ 
          width: { xs: 70, sm: 90, md: 110 },  
          height: { xs: 70, sm: 90, md: 110 },
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: '#f5f5f5',
          boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.08)',
            backgroundColor: '#e0f7fa',
          }
        }}
      >
        <img 
          src={icon} 
          alt={`${name} Icon`} 
          loading="lazy"
          style={{ width: '55%', height: '55%', objectFit: 'contain' }} 
        />
      </Box>
      <Typography 
        variant="subtitle1" 
        sx={{ mt: 2, fontWeight: 'bold', color: '#444' }}
      >
        {name}
      </Typography>
    </MuiLink>
  </Box>
);

// Home Component
const Home = () => {
  return (
    <Box className="container" sx={{ overflowX: 'hidden' }}>
      
      {/* Hero Section */}
      <Box 
        sx={{ 
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          py: { xs: 8, md: 12 },
          px: 2,
          textAlign: 'center',
          borderRadius: '8px',
          position: 'relative',
          overflow: 'hidden',
          mb: 6,
        }}
      >
        <Box 
          sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.45)', 
            py: { xs: 6, md: 8 },
            px: { xs: 2, md: 4 },
            borderRadius: '8px',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            Empowering Rural Artisans for Sustainable Livelihoods
          </Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, mb: 3, lineHeight: 1.7 }}>
            Reviving traditional crafts, fostering creativity, and creating self-reliant communities through sustainable livelihoods.
          </Typography>
          <Button 
            component={Link} 
            to="/intervention" 
            variant="contained" 
            color="secondary" 
            size="large"
            sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
          >
            Discover Our Impact
          </Button>
        </Box>

        {/* Caption */}
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute', 
            bottom: 10, 
            right: 20, 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            px: 1.5, 
            py: 0.5,
            borderRadius: '4px',
            fontSize: '0.75rem'
          }}
        >
          Rural Artisans Crafting a Brighter Future
        </Typography>
      </Box>

      {/* Slider Section */}
      <Slider />

      {/* About Us Section */}
      <Box sx={{ py: 6, textAlign: 'center', background: '#f8f8f8' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4A774B', mb: 3 }}>
          About Us
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            maxWidth: '900px', 
            mx: 'auto', 
            lineHeight: 1.8, 
            fontSize: { xs: '1rem', md: '1.125rem' },
            px: 2,
          }}
        >
          Our mission is to empower rural artisans by reviving traditional crafts and providing opportunities for sustainable livelihoods. Through hands-on training, skill enhancement, and creative pathways, we help artisans achieve self-reliance while fostering community growth.
        </Typography>
      </Box>

      {/* Image Gallery Section */}
      <Box sx={{ py: 6, px: { xs: 2, md: 4 } }}>
        <Grid container spacing={4} justifyContent="center">
          {[i1, i2].map((img, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                sx={{
                  backgroundImage: `url(${img})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: { xs: 220, sm: 250, md: 320 },
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  transition: 'transform 0.4s ease',
                  '&:hover': {
                    transform: 'scale(1.04)',
                  }
                }}
                role="img"
                aria-label={`Craft image ${index + 1}`}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Interventions Section */}
      <Box sx={{ py: 6, px: { xs: 2, md: 10 }, background: '#f5f5f5' }}>
        <Typography
          variant="h4"
          textAlign="center"
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            color: '#4A774B',
            mb: 5,
          }}
        >
          Explore Our Interventions
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {interventionData.map((intervention) => (
            <Grid item xs={6} sm={4} md={3} key={intervention.name}>
              <InterventionItem {...intervention} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Our Impact Section */}
      <Box sx={{ py: 6, textAlign: 'center', background: '#fafafa' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4A774B', mb: 3 }}>
          Our Impact
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            maxWidth: '900px', 
            mx: 'auto', 
            lineHeight: 1.8, 
            fontSize: { xs: '1rem', md: '1.125rem' },
            px: 2,
          }}
        >
          Over 300 families have been empowered through our programs, reviving extinct crafts and creating sustainable livelihoods. With your support, we continue to make a lasting impact on rural communities.
        </Typography>
      </Box>

    </Box>
  );
};

export default Home;
